import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { articleApi, scanUrl, uploadApi, campaignApi } from '../../services/api';
import QrModal from './QrModal';
import { AdminHeader, PrimaryBtn, GhostBtn } from './AdminLayout';
import { useLightbox } from '../../components/Lightbox';

export default function ArticleDetail() {
  const params = useParams();
  const location = useLocation();
  const { state } = location;
  const navigate = useNavigate();

  // /admin/campaign/:campaignId/article/new  → articleId param is absent, pathname ends with /new
  // /admin/article/:articleId                → articleId param is the real ID
  const articleId = params.articleId;
  const isNew = !articleId || location.pathname.endsWith('/article/new');
  const campaignId = state?.campaignId || params.campaignId;

  const [form, setForm] = useState({ title: '', titleEn: '', content: '', contentEn: '', images: [''] });
  const [translating, setTranslating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  const [perturbingImages, setPerturbingImages] = useState(false);
  const lb = useLightbox();
  const [variantLoading, setVariantLoading] = useState(false);
  const [qrUrl, setQrUrl] = useState(null);
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const fileInputRef = useRef(null);
  const uploadTargetIdx = useRef(null);

  useEffect(() => {
    if (!isNew && articleId) {
      articleApi.get(articleId).then((a) => {
        setForm({
          title: a.title || '',
          titleEn: a.titleEn || '',
          content: a.content || '',
          contentEn: a.contentEn || '',
          images: a.images?.length ? a.images : [''],
        });
      });
    }
  }, [articleId, isNew]);

  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const addImage = () => setField('images', [...form.images, '']);
  const removeImage = (idx) => setField('images', form.images.filter((_, i) => i !== idx));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        titleEn: form.titleEn,
        content: form.content,
        contentEn: form.contentEn,
        images: form.images.filter((u) => u.trim()),
      };
      if (isNew) {
        const created = await articleApi.create(campaignId, payload);
        navigate(`/admin/article/${created._id}`, { replace: true, state });
      } else {
        await articleApi.update(articleId, payload);
      }
    } catch (err) {
      alert('保存失败：' + (err?.response?.data?.error || err.message || '请重试'));
    } finally {
      setSaving(false);
    }
  };

  const handleTranslate = async () => {
    if (isNew) { alert('请先保存文章'); return; }
    setTranslating(true);
    try {
      const result = await articleApi.translate(articleId);
      setForm((f) => ({ ...f, titleEn: result.titleEn, contentEn: result.contentEn }));
    } catch {
      alert('翻译失败，请稍后重试');
    } finally {
      setTranslating(false);
    }
  };

  const handleRewriteContent = async () => {
    if (isNew) { alert('请先保存文章'); return; }
    setRewriting(true);
    try {
      const result = await articleApi.rewriteContent(articleId);
      setForm((f) => ({ ...f, title: result.title, content: result.content }));
    } catch {
      alert('AI 重写失败，请稍后重试');
    } finally {
      setRewriting(false);
    }
  };

  const handleVariant = async () => {
    if (isNew) { alert('请先保存文章'); return; }
    setVariantLoading(true);
    try {
      const variant = await articleApi.variant(articleId);
      navigate(`/admin/article/${variant._id}`, { state: { campaignId, campaignName: state?.campaignName } });
    } catch {
      alert('变体生成失败，请稍后重试');
    } finally {
      setVariantLoading(false);
    }
  };

  const handlePickFile = (idx) => {
    uploadTargetIdx.current = idx;
    fileInputRef.current?.click();
  };

  // 支持一次选多张：点空槽位=替换该张 + 其余追加；点「+」=全部追加。串行上传，避免一次并发太多。
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const idx = uploadTargetIdx.current;
    setUploadingIdx(idx);
    try {
      const urls = [];
      for (const f of files) urls.push(await uploadApi.image(f));
      const next = [...form.images];
      if (idx < next.length) {
        next[idx] = urls[0];
        next.push(...urls.slice(1));
      } else {
        next.push(...urls);
      }
      setField('images', next);
    } catch {
      alert('上传失败，请重试');
    } finally {
      setUploadingIdx(null);
    }
  };

  const handleRemoveImage = async (idx) => {
    const url = form.images[idx];
    setField('images', form.images.filter((_, i) => i !== idx));
    if (url) uploadApi.delete(url).catch(() => {});
  };

  const handlePerturbImages = async () => {
    if (isNew) { alert('请先保存文章'); return; }
    setPerturbingImages(true);
    try {
      const result = await articleApi.perturbImages(articleId);
      setField('images', result.images.length ? result.images : ['']);
    } catch {
      alert('图片变换失败，请稍后重试');
    } finally {
      setPerturbingImages(false);
    }
  };

  return (
    <div style={styles.page}>
      <AdminHeader
        title={isNew ? '新建文章' : '编辑文章'}
        back={true}
        actions={
          <div style={styles.headerBtns}>
            {!isNew && (
              <button style={styles.qrIconBtn} onClick={() => setQrUrl(scanUrl.article(articleId))}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <rect x="2" y="2" width="6" height="6" rx="1" stroke="#374151" strokeWidth="1.5"/>
                  <rect x="10" y="2" width="6" height="6" rx="1" stroke="#374151" strokeWidth="1.5"/>
                  <rect x="2" y="10" width="6" height="6" rx="1" stroke="#374151" strokeWidth="1.5"/>
                  <path d="M10 10h2v2h-2zM14 10v2M10 14h2M14 14v2M12 12h4" stroke="#374151" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            )}
            {!isNew && (
              <button
                style={variantLoading ? styles.varBtnLoading : styles.varBtn}
                disabled={variantLoading}
                onClick={handleVariant}
              >
                {variantLoading ? '生成中...' : '一键变体'}
              </button>
            )}
            <PrimaryBtn onClick={handleSave} disabled={saving}>
              {saving ? '保存中...' : '保存'}
            </PrimaryBtn>
          </div>
        }
      />

      <div style={styles.body}>
        <div style={styles.section}>
          <label style={styles.label}>标题</label>
          <input
            style={styles.input}
            value={form.title}
            onChange={(e) => setField('title', e.target.value)}
            placeholder="输入文章标题"
          />
        </div>

        <div style={styles.section}>
          <div style={styles.labelRow}>
            <label style={styles.label}>正文</label>
            <button style={rewriting ? styles.aiBtnLoading : styles.aiBtn} disabled={rewriting} onClick={handleRewriteContent}>
              {rewriting ? 'AI 重写中...' : 'AI 重写'}
            </button>
          </div>
          <textarea
            style={styles.textarea}
            value={form.content}
            onChange={(e) => setField('content', e.target.value)}
            placeholder="输入正文内容，支持 #话题 标签"
            rows={8}
          />
        </div>

        <div style={styles.section}>
          <div style={styles.labelRow}>
            <label style={styles.label}>英文内容 <span style={styles.enTag}>EN</span></label>
            <button
              style={translating ? styles.aiBtnLoading : styles.aiBtn}
              disabled={translating}
              onClick={handleTranslate}
            >
              {translating ? '翻译中...' : 'AI 翻译'}
            </button>
          </div>
          <input
            style={{ ...styles.input, marginBottom: 8 }}
            value={form.titleEn}
            onChange={(e) => setField('titleEn', e.target.value)}
            placeholder="English title"
          />
          <textarea
            style={styles.textarea}
            value={form.contentEn}
            onChange={(e) => setField('contentEn', e.target.value)}
            placeholder="English content..."
            rows={6}
          />
        </div>

        <div style={styles.section}>
          <div style={styles.labelRow}>
            <label style={styles.label}>图片</label>
            <button style={perturbingImages ? styles.aiBtnLoading : styles.aiBtn} disabled={perturbingImages} onClick={handlePerturbImages}>
              {perturbingImages ? '处理中...' : 'AI 变换图片'}
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <div style={styles.imageGrid}>
            {form.images.map((url, idx) => (
              <div key={idx} style={styles.imageSlot}>
                {uploadingIdx === idx ? (
                  <div style={styles.uploadingOverlay}>
                    <div style={styles.spinner} />
                    <span style={styles.uploadingText}>上传中...</span>
                  </div>
                ) : url ? (
                  <>
                    <img
                      src={url} alt="" style={styles.imagePreview}
                      onClick={() => lb.open(form.images.filter(Boolean), form.images.filter(Boolean).indexOf(url))}
                      onError={(e) => { e.target.style.opacity = 0.3; }}
                    />
                    <button style={styles.removeX} onClick={() => handleRemoveImage(idx)}>
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path d="M1 1l8 8M9 1L1 9" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    </button>
                  </>
                ) : (
                  <div style={styles.emptySlot} onClick={() => handlePickFile(idx)}>
                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                      <path d="M11 4v14M4 11h14" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                )}
              </div>
            ))}
            {uploadingIdx !== form.images.length && (
              <div style={styles.addSlot} onClick={() => handlePickFile(form.images.length)}>
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                  <path d="M11 4v14M4 11h14" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
            )}
            {uploadingIdx === form.images.length && (
              <div style={styles.imageSlot}>
                <div style={styles.uploadingOverlay}>
                  <div style={styles.spinner} />
                  <span style={styles.uploadingText}>上传中...</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={{ height: 32 }} />
      </div>

      {lb.node}
      {qrUrl && <QrModal url={qrUrl} title="文章二维码" onClose={() => setQrUrl(null)} />}
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#f7f7f8', display: 'flex', flexDirection: 'column' },
  headerBtns: { display: 'flex', alignItems: 'center', gap: 8 },
  qrIconBtn: {
    width: 36, height: 36, border: '1px solid #e5e7eb', borderRadius: 8,
    background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  varBtn: {
    padding: '8px 14px', background: '#111', color: '#fff',
    border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap',
  },
  varBtnLoading: {
    padding: '8px 14px', background: '#6b7280', color: '#fff',
    border: 'none', borderRadius: 8, cursor: 'default', fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap',
  },

  body: { flex: 1, padding: '12px 12px 24px', display: 'flex', flexDirection: 'column', gap: 10 },
  section: {
    background: '#fff', borderRadius: 12, padding: '14px 14px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  },
  label: { fontSize: 13, fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 },
  enTag: { fontSize: 10, fontWeight: 700, color: '#fff', background: '#4285f4', borderRadius: 3, padding: '1px 5px' },
  labelRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  input: {
    width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb',
    borderRadius: 8, fontSize: 15, outline: 'none', boxSizing: 'border-box', color: '#111',
  },
  textarea: {
    width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb',
    borderRadius: 8, fontSize: 15, outline: 'none', resize: 'vertical',
    fontFamily: 'inherit', color: '#111', boxSizing: 'border-box', lineHeight: 1.7,
  },
  aiBtn: {
    padding: '5px 12px', border: '1px solid #e5e7eb', borderRadius: 6,
    background: '#f9fafb', color: '#374151', cursor: 'pointer', fontSize: 12, fontWeight: 500,
  },
  aiBtnLoading: {
    padding: '5px 12px', border: '1px solid #e5e7eb', borderRadius: 6,
    background: '#f3f4f6', color: '#9ca3af', cursor: 'default', fontSize: 12,
  },
  imageGrid: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  imageSlot: {
    width: 88, height: 88, borderRadius: 10, overflow: 'hidden',
    position: 'relative', background: '#f3f4f6', flexShrink: 0,
  },
  imagePreview: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  emptySlot: {
    width: '100%', height: '100%', border: '1.5px dashed #d1d5db', borderRadius: 10,
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxSizing: 'border-box', background: '#fafafa',
  },
  removeX: {
    position: 'absolute', top: 4, right: 4,
    width: 20, height: 20, borderRadius: '50%', border: 'none',
    background: 'rgba(0,0,0,0.5)', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
  },
  uploadingOverlay: {
    position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.88)',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
  },
  spinner: {
    width: 20, height: 20, border: '2px solid #e5e7eb', borderTopColor: '#FA1C51',
    borderRadius: '50%', animation: 'spin 0.8s linear infinite',
  },
  uploadingText: { fontSize: 10, color: '#9ca3af' },
  addSlot: {
    width: 88, height: 88, flexShrink: 0, borderRadius: 10, border: '1.5px dashed #d1d5db',
    background: '#fafafa', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
};
